<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import EvolutionInstancesAPI from 'dashboard/api/evolutionInstances';
import SettingsSection from 'dashboard/components/SettingsSection.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ConfirmButton from 'dashboard/components-next/button/ConfirmButton.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

const POLL_INTERVAL = 3000;
const { t } = useI18n();
const status = ref(
  props.inbox.additional_attributes?.evolution_connection_status || 'unknown'
);
const qrCode = ref('');
const pairingCode = ref('');
const isLoadingStatus = ref(false);
const isGeneratingQr = ref(false);
const isDisconnecting = ref(false);
let pollingTimer = null;

const isConnected = computed(() => status.value === 'open');
const statusLabel = computed(() => {
  if (isLoadingStatus.value) {
    return t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.CHECKING_STATUS');
  }

  if (status.value === 'open') {
    return t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.CONNECTED_STATUS');
  }
  if (status.value === 'connecting') {
    return t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.CONNECTING_STATUS');
  }
  if (status.value === 'close') {
    return t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.DISCONNECTED_STATUS');
  }
  return t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.UNKNOWN_STATUS');
});

const errorMessage = (error, fallbackMessage) =>
  error.response?.data?.message || error.message || fallbackMessage;

const stopPolling = () => {
  if (!pollingTimer) return;

  window.clearInterval(pollingTimer);
  pollingTimer = null;
};

const fetchStatus = async ({ showError = true } = {}) => {
  try {
    const { data } = await EvolutionInstancesAPI.status(props.inbox.id);
    status.value = data.status;
    if (data.connected) {
      qrCode.value = '';
      pairingCode.value = '';
      stopPolling();
    }
  } catch (error) {
    stopPolling();
    if (showError) {
      useAlert(
        errorMessage(error, t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.STATUS_ERROR'))
      );
    }
  }
};

const startPolling = () => {
  stopPolling();
  pollingTimer = window.setInterval(
    () => fetchStatus({ showError: false }),
    POLL_INTERVAL
  );
};

const generateQrCode = async () => {
  isGeneratingQr.value = true;
  try {
    const { data } = await EvolutionInstancesAPI.connect(props.inbox.id);
    status.value = data.status || 'connecting';
    qrCode.value = data.qr_code || '';
    pairingCode.value = data.pairing_code || '';
    if (data.connected) {
      useAlert(t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.ALREADY_CONNECTED'));
    } else {
      startPolling();
    }
  } catch (error) {
    useAlert(
      errorMessage(error, t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.QR_ERROR'))
    );
  } finally {
    isGeneratingQr.value = false;
  }
};

const disconnect = async () => {
  isDisconnecting.value = true;
  try {
    const { data } = await EvolutionInstancesAPI.disconnect(props.inbox.id);
    status.value = data.status;
    qrCode.value = '';
    pairingCode.value = '';
    stopPolling();
    useAlert(t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.DISCONNECT_SUCCESS'));
  } catch (error) {
    useAlert(
      errorMessage(
        error,
        t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.DISCONNECT_ERROR')
      )
    );
  } finally {
    isDisconnecting.value = false;
  }
};

onMounted(async () => {
  isLoadingStatus.value = true;
  await fetchStatus();
  isLoadingStatus.value = false;
});
onBeforeUnmount(stopPolling);
</script>

<template>
  <div class="mx-8">
    <SettingsSection
      :title="$t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.SETTINGS_TITLE')"
      :sub-title="$t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.SETTINGS_DESCRIPTION')"
    >
      <div class="flex w-full max-w-3xl flex-col gap-5">
        <div
          class="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-n-weak bg-n-alpha-1 p-4"
        >
          <div class="flex items-center gap-3">
            <div
              class="flex size-10 items-center justify-center rounded-lg bg-n-alpha-2"
            >
              <Icon icon="i-woot-whatsapp" class="size-5 text-n-brand" />
            </div>
            <div>
              <p class="text-sm font-medium text-n-slate-12">
                {{ inbox.name }}
              </p>
              <p class="text-sm text-n-slate-11">
                {{ statusLabel }}
              </p>
            </div>
          </div>

          <div class="flex flex-wrap gap-2">
            <NextButton
              v-if="!isConnected"
              solid
              green
              icon="i-lucide-qr-code"
              :is-loading="isGeneratingQr"
              :label="$t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.GENERATE_NEW_QR')"
              @click="generateQrCode"
            />
            <ConfirmButton
              v-else
              variant="outline"
              color="slate"
              confirm-color="ruby"
              icon="i-lucide-unplug"
              :is-loading="isDisconnecting"
              :label="$t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.DISCONNECT')"
              :confirm-label="
                $t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.CONFIRM_DISCONNECT')
              "
              @click="disconnect"
            />
          </div>
        </div>

        <div v-if="qrCode" class="flex flex-col items-center gap-4 text-center">
          <div class="rounded-lg bg-white p-3">
            <img
              :src="qrCode"
              :alt="$t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.QR_ALT')"
              class="size-64 max-h-[70vw] max-w-[70vw]"
            />
          </div>
          <div>
            <p class="font-medium text-n-slate-12">
              {{ $t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.SCAN_TITLE') }}
            </p>
            <p class="mt-1 text-sm text-n-slate-11">
              {{ $t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.SCAN_DESCRIPTION') }}
            </p>
            <p
              v-if="pairingCode"
              class="mt-3 font-mono text-lg text-n-slate-12"
            >
              {{ pairingCode }}
            </p>
          </div>
        </div>
      </div>
    </SettingsSection>
  </div>
</template>
